import { Type, Static } from '@sinclair/typebox';

/**
 * Событие состояния фоновой задачи в WebSocket-канале 'background-tasks'.
 * Сервер шлёт его создателю задачи (created_by) при каждом изменении строки
 * (afterCreate/afterUpdate). Общая схема для сервера (rules.ts) и клиента
 * (executeBackgroundTask).
 */
export const BackgroundTaskEventSchema = Type.Object({
  /** uuid задачи — по нему клиент фильтрует «свой» запуск. */
  uuid: Type.String(),
  /** Тип задачи (ключ реестра). */
  taskType: Type.String(),
  /** Вычисляемый статус. */
  status: Type.Union([
    Type.Literal('queued'),
    Type.Literal('running'),
    Type.Literal('done'),
    Type.Literal('failed'),
  ]),
  /** Прогресс 0..100 (если задача его сообщала). */
  percent: Type.Optional(Type.Number()),
  /** Текстовое сообщение о ходе (ctx.send). */
  message: Type.Optional(Type.String()),
  /** Результат — присутствует на статусе done (для отчёта: { url, fileId }). */
  result: Type.Optional(Type.Unknown()),
  /** Текст ошибки — присутствует на статусе failed. */
  error: Type.Optional(Type.String()),
});

export type BackgroundTaskEvent = Static<typeof BackgroundTaskEventSchema>;
export type BackgroundTaskStatus = BackgroundTaskEvent['status'];

/**
 * Вычислить статус задачи из её полей — единственный источник статуса.
 * Чистая функция без сайд-эффектов; типизирована на структуру полей, чтобы
 * общий модуль не зависел от серверных типов. Приоритет: error → finished → started.
 *
 * Примечание: `error != null` — наличие ошибки (в т.ч. пустая строка) = failed,
 * консистентно с SQL `error IS NOT NULL` (его введём для админ-таблицы позже).
 */
export function computeTaskStatus(task: {
  started_at?: string | Date | null;
  finished_at?: string | Date | null;
  error?: string | null;
}): BackgroundTaskStatus {
  if (task.error != null) return 'failed';
  if (task.finished_at != null) return 'done';
  if (task.started_at != null) return 'running';
  return 'queued';
}
